import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, PackageMinus, Unlink } from "lucide-react";
import React, { Suspense } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  dissolveCargoPallet,
  getBoxesConfig,
  getCargo,
  removeCargoFromPallet,
  takeCargoPiece,
  type CargoView,
} from "@/api/boxes";
import { groupSscc } from "@/lib/boxes";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { useBackGuard } from "@/lib/scan-ui/back-guard";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { BottomSheet } from "@/lib/scan-ui/overlays";
import { apiErrorText, LoadState, useGoBack } from "@/lib/scan-ui/screen";
import { useScanCapture } from "@/lib/use-scan-capture";
import { ScanBanner } from "@/pages/fbs/ScanBanner";
import { boxesWord, CargoStatusChip, fmtWhen, OriginBadge } from "@/pages/boxes/cargo-ui";

type Banner = { kind: "ok" | "error" | "warn"; text: string } | null;
type Confirm = "remove" | "dissolve" | null;

/**
 * Маршрут карточки: экран монтируется ЗАНОВО на каждый код (`key={code}`).
 *
 * ⚠️ React Router при смене `:code` переиспользует компонент, и состояние
 * экрана — режим «Вынуть штуки», плашка, подтверждение — переживало переход:
 * с паллеты в её короб, из короба A в короб B «Вынуть штуки» оставался
 * включённым на ЧУЖОМ коробе, и следующий скан вынимал бы штуку не там, где
 * человек думает.
 */
export function CargoRoute(): React.ReactElement {
  const { code = "" } = useParams();
  return <CargoPage key={code} />;
}

/**
 * Карточка грузоместа с упаковки: короб или паллета chz-station.
 *
 * Один экран на оба вида; действия — по виду и статусу, список приходит с бэка
 * (`actions`), экран сам не решает, что можно:
 *
 * * короб на паллете — «Снять с паллеты»;
 * * паллета — «Расформировать» (снять все короба);
 * * короб — «Вынуть штуки»: режим, где сканируют DataMatrix штук этого короба.
 *
 * Каждое действие пишет историю и журнал для L3 на бэке. Аннулированному и
 * пустому действий нет — только справка.
 */
export function CargoPage(): React.ReactElement {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const goBack = useGoBack("/boxes?tab=packing");
  const [params] = useSearchParams();
  const [banner, setBanner] = React.useState<Banner>(null);
  const [confirm, setConfirm] = React.useState<Confirm>(null);
  // Режим «Вынуть штуки» включается кнопкой или приезжает из «Взять из короба»
  // (`?take=1`) — человек за этим и сканировал короб.
  const [taking, setTaking] = React.useState(params.get("take") === "1");
  const [cameraOpen, setCameraOpen] = React.useState(false);

  usePreloadChunk(CameraScanner);
  const card = useQuery({
    queryKey: ["cargo", code],
    queryFn: () => getCargo(code),
    enabled: Boolean(code),
  });
  const config = useQuery({ queryKey: ["boxes-config"], queryFn: getBoxesConfig });
  const cameraAllowed =
    config.data?.scan_method === "camera" || config.data?.scan_method === "both";
  const d = card.data;
  const canTake = Boolean(d?.actions.includes("take_pieces"));
  const takeMode = taking && canTake;

  useBackGuard(cameraOpen, () => setCameraOpen(false));

  const put = (view: CargoView) => {
    qc.setQueryData(["cargo", code], view);
    void qc.invalidateQueries({ queryKey: ["cargo-list"] });
  };

  const take = useMutation({
    mutationFn: (raw: string) => takeCargoPiece(code, raw),
    onSuccess: (view) => {
      put(view);
      setBanner({
        kind: view.status === "empty" ? "warn" : "ok",
        text:
          view.status === "empty"
            ? "Вынута последняя штука — короб пуст, наклейку снимите."
            : `Штука вынута. В коробе осталось ${view.left} из ${view.quantity}.`,
      });
      if (view.status === "empty") setTaking(false);
    },
    onError: (e) => setBanner({ kind: "error", text: apiErrorText(e) }),
  });

  const remove = useMutation({
    mutationFn: () => removeCargoFromPallet(code),
    onSuccess: (view) => {
      put(view);
      setConfirm(null);
      setBanner({ kind: "ok", text: "Короб снят с паллеты." });
    },
    onError: (e) => {
      setConfirm(null);
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  const dissolve = useMutation({
    mutationFn: () => dissolveCargoPallet(code),
    onSuccess: (view) => {
      put(view);
      setConfirm(null);
      setBanner({ kind: "ok", text: "Паллета расформирована: все короба сняты." });
    },
    onError: (e) => {
      setConfirm(null);
      setBanner({ kind: "error", text: apiErrorText(e) });
    },
  });

  const capture = useScanCapture({
    enabled: takeMode && !take.isPending && !cameraOpen && !confirm,
    onScan: (raw) => take.mutate(raw),
    // ⚠️ Код пришёл, когда перехват выключен, — штука НЕ вынута. Молчать
    // нельзя: человек решит, что отметил её.
    onBlocked: () =>
      setBanner({
        kind: "warn",
        text: take.isPending
          ? "Предыдущий скан ещё записывается — отсканируйте ещё раз."
          : canTake
            ? "Скан не засчитан: чтобы вынуть штуку, нажмите «Вынуть штуки»."
            : "Скан не засчитан: с этим грузоместом действий нет.",
      }),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <input ref={capture.scanInputRef} {...capture.scanInputProps} className="sr-only" />

      <div className="flex items-center gap-3">
        <button onClick={goBack} className="text-sm text-slate-500" aria-label="Назад">
          ←
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">
              {d?.kind === "pallet" ? "Паллета" : "Короб"}
            </span>
            <OriginBadge origin="packing" />
          </div>
          <div className="break-all font-mono text-lg">{groupSscc(code)}</div>
        </div>
      </div>

      {banner && (
        <ScanBanner tone={banner.kind} title={banner.text} onClose={() => setBanner(null)} />
      )}

      {(card.isLoading || card.error) && (
        <LoadState
          isLoading={card.isLoading}
          error={card.error}
          onRetry={() => void card.refetch()}
        />
      )}

      {d && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <CargoStatusChip status={d.status} text={d.status_text} />
            {d.kind === "box" && d.pallet_code && (
              <button
                className="text-xs text-slate-600 underline"
                onClick={() => navigate(`/boxes/cargo/${d.pallet_code}`)}
              >
                на паллете {groupSscc(d.pallet_code)}
              </button>
            )}
          </div>

          {takeMode && (
            <div className="rounded-lg border-2 border-sky-300 bg-sky-50 p-3 text-sm text-sky-900">
              <div className="font-medium">Вынуть штуки</div>
              <p className="mt-1">
                Сканируйте DataMatrix штук, которые забираете из этого короба. Чужой
                код не засчитается.
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                {cameraAllowed && (
                  <Button variant="outline" onClick={() => setCameraOpen(true)}>
                    <Camera className="mr-1 size-4" /> Камерой
                  </Button>
                )}
                <Button variant="outline" onClick={() => setTaking(false)}>
                  Готово
                </Button>
              </div>
            </div>
          )}

          <CargoActions
            view={d}
            takeMode={takeMode}
            busy={remove.isPending || dissolve.isPending}
            onTake={() => {
              setBanner(null);
              setTaking(true);
            }}
            onConfirm={setConfirm}
          />

          <section className="rounded-lg border bg-white">
            <div className="border-b px-3 py-2 text-sm font-medium">
              Внутри · {d.left} из {d.quantity} шт
            </div>
            {d.items.length === 0 && (
              <div className="p-4 text-sm text-slate-500">Пусто.</div>
            )}
            {d.items.map((i) => (
              <div
                key={`${i.nomenclature_ref}|${i.characteristic_ref}|${i.partia_ref}`}
                className="border-b px-3 py-2 last:border-0"
              >
                <div className="break-words text-sm">
                  {i.nomenclature_name || i.nomenclature_article || i.nomenclature_ref}
                </div>
                <div className="text-xs text-slate-500">
                  {i.characteristic_name || "без характеристики"} ·{" "}
                  {i.partia_name ? `партия ${i.partia_name}` : "без партии"} · {i.left}
                  {i.left !== i.quantity ? ` из ${i.quantity}` : ""} шт
                </div>
              </div>
            ))}
          </section>

          {d.kind === "pallet" && d.boxes.length > 0 && (
            <section className="rounded-lg border bg-white">
              <div className="border-b px-3 py-2 text-sm font-medium">
                Короба ({d.boxes.length})
              </div>
              {d.boxes.map((b) => (
                <button
                  key={b.code}
                  onClick={() => navigate(`/boxes/cargo/${b.code}`)}
                  className="flex w-full items-center gap-2 border-b px-3 py-2 text-left last:border-0 hover:bg-slate-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm">{groupSscc(b.code)}</div>
                    <div className="text-xs text-slate-500">
                      {b.left} из {b.quantity} шт
                    </div>
                  </div>
                  <CargoStatusChip status={b.status} text={b.status_text} className="shrink-0" />
                </button>
              ))}
            </section>
          )}

          {d.kind === "box" && d.pieces.length > 0 && (
            <details className="rounded-lg border bg-white" open={takeMode}>
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
                Коды штук ({d.pieces.length}
                {d.quantity !== d.left ? `, вынуто ${d.quantity - d.left}` : ""})
              </summary>
              {d.pieces.map((p) => (
                <div key={p.code} className="border-t px-3 py-1.5">
                  <div
                    className={
                      p.taken
                        ? "break-all font-mono text-xs text-slate-400 line-through"
                        : "break-all font-mono text-xs"
                    }
                  >
                    {p.code}
                  </div>
                  {p.taken && (
                    <div className="text-[11px] text-amber-700">
                      вынута · {p.taken_section_title || "раздел не записан"}
                      {p.taken_by ? ` · ${p.taken_by}` : ""} · {fmtWhen(p.taken_at)}
                    </div>
                  )}
                </div>
              ))}
            </details>
          )}

          <section className="rounded-lg border bg-white p-3">
            <div className="mb-2 text-sm font-medium">История</div>
            <ol className="space-y-2">
              {d.history.map((h, idx) => (
                <li key={idx} className="text-xs">
                  <div className="text-slate-800">{h.text}</div>
                  <div className="text-slate-500">
                    {fmtWhen(h.at)}
                    {h.section_title ? ` · ${h.section_title}` : ""}
                    {h.user_name && h.kind !== "received" ? ` · ${h.user_name}` : ""}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </>
      )}

      {confirm && d && (
        <BottomSheet onClose={() => setConfirm(null)} panelClassName="p-4">
          <div className="mb-1 text-sm font-semibold">
            {confirm === "dissolve" ? "Расформировать паллету?" : "Снять короб с паллеты?"}
          </div>
          <p className="mb-4 text-sm text-slate-600">
            {confirm === "dissolve"
              ? `С паллеты ${groupSscc(d.code)} будут сняты все короба: ${boxesWord(d.boxes.length)}. ` +
                "Паллета останется пустой. Вернуть короба на неё нельзя."
              : `Короб ${groupSscc(d.code)} будет снят с паллеты ${groupSscc(d.pallet_code)}. ` +
                "Вернуть его на паллету нельзя."}
          </p>
          <Button
            className="w-full"
            disabled={remove.isPending || dissolve.isPending}
            onClick={() => (confirm === "dissolve" ? dissolve.mutate() : remove.mutate())}
          >
            {confirm === "dissolve" ? "Расформировать" : "Снять с паллеты"}
          </Button>
          <Button variant="ghost" className="mt-2 w-full" onClick={() => setConfirm(null)}>
            Отмена
          </Button>
        </BottomSheet>
      )}

      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-[80] bg-black">
            <CameraScanner
              onDetect={(raw) => {
                setCameraOpen(false);
                take.mutate(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}

/** Кнопки действий — ровно те, что разрешил бэк (`actions`); столбцом на узком экране. */
function CargoActions({
  view,
  takeMode,
  busy,
  onTake,
  onConfirm,
}: {
  view: CargoView;
  takeMode: boolean;
  busy: boolean;
  onTake: () => void;
  onConfirm: (c: Confirm) => void;
}): React.ReactElement | null {
  const has = (a: CargoView["actions"][number]) => view.actions.includes(a);
  if (view.actions.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        {view.status === "voided"
          ? "Грузоместо аннулировано на упаковке — действий нет, наклейку снимите."
          : view.status === "dissolved"
            ? "Паллета расформирована — короба сканируйте по одному."
            : "Грузоместо пустое — действий нет."}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      {has("take_pieces") && !takeMode && (
        <Button className="sm:flex-1" onClick={onTake}>
          <PackageMinus className="mr-1 size-4" /> Вынуть штуки
        </Button>
      )}
      {has("remove_from_pallet") && (
        <Button
          variant="outline"
          className="sm:flex-1"
          disabled={busy}
          onClick={() => onConfirm("remove")}
        >
          <Unlink className="mr-1 size-4" /> Снять с паллеты
        </Button>
      )}
      {has("dissolve") && (
        <Button
          variant="outline"
          className="sm:flex-1"
          disabled={busy}
          onClick={() => onConfirm("dissolve")}
        >
          <Unlink className="mr-1 size-4" /> Расформировать
        </Button>
      )}
    </div>
  );
}
